import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetSecret,
  getAllCardsWithListNames,
  getUserInfo,
} from "../../../api/trello";
import { createErrorLog } from "../../../api/index";

export const fetchLists = createAsyncThunk(
  "trello/fetchLists",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const t = window.TrelloPowerUp.iframe({
        appKey: "035539076201571631d2a69fa82de83d",
        appName: "Board2Export by Ezy PowerUps",
      });

      const secret = await GetSecret(t);
      if (!secret) throw new Error("Failed to get Trello secret");

      window.Trello.setToken(secret);

      const board = await t.board("all");
      const boardId = board.id;

      const trelloLists = await new Promise((resolve, reject) => {
        window.Trello.get(
          `/boards/${boardId}/lists`,
          { fields: "id,name" },
          (lists) => {
            if (lists && lists.length > 0) {
              resolve(lists);
            } else {
              resolve([]);
            }
          }
        );
      });

      return trelloLists.map((list) => ({
        name: list.name,
        value: list.id,
      }));
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchBoardName = createAsyncThunk(
  "trello/fetchBoardName",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const t = window.TrelloPowerUp.iframe({
        appKey: "035539076201571631d2a69fa82de83d",
        appName: "Board2Export by Ezy PowerUps",
      });

      const secret = await GetSecret(t);
      if (!secret) throw new Error("Failed to get Trello secret");

      window.Trello.setToken(secret);

      const board = await t.board("all");
      const boardName = board.name;
      const boardId = board.id;

      if (!boardName) throw new Error("Failed to fetch board name");

      return { boardName, boardId };
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserInfo = createAsyncThunk(
  "trello/fetchUserInfo",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const t = window.TrelloPowerUp.iframe({
        appKey: "035539076201571631d2a69fa82de83d",
        appName: "Board2Export by Ezy PowerUps",
      });

      const userInfo = await getUserInfo(t);

      return userInfo;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCardsWithListNames = createAsyncThunk(
  "trello/fetchCardsWithListNames",
  async (_, { getState, rejectWithValue }) => {
    const { email } = getState().trello;
    try {
      const t = window.TrelloPowerUp.iframe({
        appKey: "035539076201571631d2a69fa82de83d",
        appName: "Board2Export by Ezy PowerUps",
      });

      const secret = await GetSecret(t);
      if (!secret) throw new Error("Failed to get Trello secret");

      window.Trello.setToken(secret);

      const board = await t.board("all");
      const boardId = board.id;

      const cardsWithListNames = await getAllCardsWithListNames(t, boardId);

      return cardsWithListNames;
    } catch (error) {
      const err = {
        message: error?.message,
        tracer: error,
        email,
        url: error?.url || "not found",
      };

      await createErrorLog(err);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  name: "",
  email: "",
  image: "",
  token: "",
  fetched: false,
  listsFetched: false,
  getUserLoading: false,
  boardName: "",
  boardId: null,
  lists: [],
  listsLoading: false,
  cards: [],
  cardsLoading: false,
  cardsFetched: false,
  isGeneratingTasks: false,
  genSuccess: false,
  genFailure: false,
};

const trelloSlice = createSlice({
  name: "trello",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLists.pending, (state) => {
        state.listsLoading = true;
        state.listsFetched = false;
      })
      .addCase(fetchLists.fulfilled, (state, action) => {
        state.listsLoading = false;
        state.listsFetched = true;
        state.lists = action.payload;
      })
      .addCase(fetchLists.rejected, (state) => {
        state.listsLoading = false;
        state.listsFetched = true;
        state.lists = [];
      })
      .addCase(fetchCardsWithListNames.pending, (state) => {
        state.cardsLoading = true;
        state.cardsFetched = false;
      })
      .addCase(fetchCardsWithListNames.fulfilled, (state, action) => {
        state.cardsLoading = false;
        state.cardsFetched = true;
        state.cards = action.payload;
      })
      .addCase(fetchCardsWithListNames.rejected, (state) => {
        state.cardsLoading = false;
        state.cardsFetched = true;
        state.cards = [];
      })
      .addCase(fetchUserInfo.pending, (state) => {
        state.getUserLoading = true;
        state.fetched = false;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.getUserLoading = false;
        state.name = action.payload.name;
        state.email = action.payload.email;
        state.image = action.payload.image;
        state.token = action.payload.token;
        state.fetched = true;
      })
      .addCase(fetchUserInfo.rejected, (state) => {
        state.getUserLoading = false;
        state.name = null;
        state.email = null;
        state.image = "";
        state.fetched = true;
      })
      .addCase(fetchBoardName.fulfilled, (state, action) => {
        state.boardName = action.payload.boardName;
        state.boardId = action.payload.boardId;
      });
  },
});

export default trelloSlice.reducer;
